import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { WebSocketProvider } from "@/context/WebSocketContext";
import Layout from "@/components/Layout";

// Initialize monitoring
import { initSentry, initPostHog, SentryErrorBoundary } from "@/lib/monitoring";
initSentry();
initPostHog();

import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import LeadsPage from "@/pages/LeadsPage";
import ClientsPage from "@/pages/ClientsPage";
import ParksPage from "@/pages/ParksPage";
import JobsPage from "@/pages/JobsPage";
import CasesPage from "@/pages/CasesPage";
import ClaimsPage from "@/pages/ClaimsPage";
import ReportsPage from "@/pages/ReportsPage";
import ReportingDashboardPage from "@/pages/ReportingDashboardPage";
import QuotesPage from "@/pages/QuotesPage";
import InvoicesPage from "@/pages/InvoicesPage";
import ContractorsPage from "@/pages/ContractorsPage";
import ContractorProfilePage from "@/pages/ContractorProfilePage";
import TasksPage from "@/pages/TasksPage";
import StaffPage from "@/pages/StaffPage";
import SurveyTemplatesPage from "@/pages/SurveyTemplatesPage";
import MobileSurveyPage from "@/pages/MobileSurveyPage";
import ClientPortalManagementPage from "@/pages/ClientPortalManagementPage";
import SettingsPage from "@/pages/SettingsPage";
import DocumentsPage from "@/pages/DocumentsPage";
import QuoteResponsePage from "@/pages/QuoteResponsePage";
import ContractorPortalPage from "@/pages/ContractorPortalPage";
import ClientPortalPage from "@/pages/ClientPortalPage";
import CalendarPage from "@/pages/CalendarPage";
import FMDashboardPage from "@/pages/FMDashboardPage";
import PPMDashboardPage from "@/pages/PPMDashboardPage";
import PPMLibraryPage from "@/pages/PPMLibraryPage";
import PPMSchedulePage from "@/pages/PPMSchedulePage";
import PPMJobsPage from "@/pages/PPMJobsPage";
import AssetsPage from "@/pages/AssetsPage";
import ContractsDashboardPage from "@/pages/ContractsDashboardPage";
import ClientReportingPage from "@/pages/ClientReportingPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import QuotedWorksPage from "@/pages/QuotedWorksPage";
import VariationsPage from "@/pages/VariationsPage";
import InvoiceControlPage from "@/pages/InvoiceControlPage";
import PortalDashboardPage from "@/pages/PortalDashboardPage";
import PortalJobsPage from "@/pages/PortalJobsPage";
import PortalReportPage from "@/pages/PortalReportPage";
import PortalPPMPage from "@/pages/PortalPPMPage";
import FMJobDetailPage from "@/pages/FMJobDetailPage";
import AssetDetailPage from "@/pages/AssetDetailPage";
import JobDetailPage from "@/pages/JobDetailPage";
import ContractorJobPage from "@/pages/ContractorJobPage";
import ContractorJobsListPage from "@/pages/ContractorJobsListPage";
import OperationalDashboardPage from "@/pages/OperationalDashboardPage";
import ClientDashboardPage from "@/pages/ClientDashboardPage";
import OperationsRoutesPage from "@/pages/OperationsRoutesPage";
import OperationsAttendancePage from "@/pages/OperationsAttendancePage";
import OperationsAutomationPage from "@/pages/OperationsAutomationPage";
import CommunicationMetricsPage from "@/pages/CommunicationMetricsPage";
import AdvancedAnalyticsPage from "@/pages/AdvancedAnalyticsPage";
import FMReportingPage from "@/pages/FMReportingPage";
import ContractorPerformanceDetailPage from "@/pages/ContractorPerformanceDetailPage";
import ClientReportPage from "@/pages/ClientReportPage";
import LiveIssueTrackerPage from "@/pages/LiveIssueTrackerPage";
import SmartJobIntakePage from "@/pages/SmartJobIntakePage";
import ClientJobRequestPage from "@/pages/ClientJobRequestPage";
import MobileQuickReportPage from "@/pages/MobileQuickReportPage";
import ReadinessAuditPage from "@/pages/ReadinessAuditPage";
import DataCleanupPage from "@/pages/DataCleanupPage";
import TestJobPurgePage from "@/pages/TestJobPurgePage";
import DataMergeDashboardPage from "@/pages/DataMergeDashboardPage";
import GlobalActionQueuePage from "@/pages/GlobalActionQueuePage";
import LockdownDashboardPage from "@/pages/LockdownDashboardPage";
import TestDataPurgePage from "@/pages/TestDataPurgePage";
import QuickFixWizardPage from "@/pages/QuickFixWizardPage";
import PressureTestPage from "@/pages/PressureTestPage";
import PPMCompliancePage from "@/pages/PPMCompliancePage";
import PPMWorkloadPage from "@/pages/PPMWorkloadPage";
import PPMBulkAssignPage from "@/pages/PPMBulkAssignPage";
import ClientPortalV2DashboardPage from "@/pages/ClientPortalV2DashboardPage";
import ClientPortalV2JobsPage from "@/pages/ClientPortalV2JobsPage";
import ClientPortalV2PPMPage from "@/pages/ClientPortalV2PPMPage";
import ClientPortalV2QuotesPage from "@/pages/ClientPortalV2QuotesPage";
import EdgeCaseAuditPage from "@/pages/EdgeCaseAuditPage";
import ClientLoginPage from "@/pages/ClientLoginPage";
import ClientPortalV2AuthDashboard from "@/pages/ClientPortalV2AuthDashboard";
import ClientJobsPage from "@/pages/ClientJobsPage";
import ClientJobDetailPage from "@/pages/ClientJobDetailPage";
import ClientQuotesPage from "@/pages/ClientQuotesPage";
import ClientQuoteDetailPage from "@/pages/ClientQuoteDetailPage";
import ClientInvoicesPage from "@/pages/ClientInvoicesPage";
import ClientDocumentsPage from "@/pages/ClientDocumentsPage";
import LHSCommandCentre from "@/pages/LHSCommandCentre";
import BoardPackDashboard from "@/pages/BoardPackDashboard";
import ContractorCompanyLogin from "@/pages/ContractorCompanyLogin";
import ContractorCompanyDashboard from "@/pages/ContractorCompanyDashboard";
import ContractorCompanyJobs from "@/pages/ContractorCompanyJobs";
import ContractorCompanyPerformance from "@/pages/ContractorCompanyPerformance";
import ContractorJobDetail from "@/pages/ContractorJobDetail";
import ContractorRespondPage from "@/pages/contractor/ContractorRespondPage";
import PipelineBoard from "@/pages/PipelineBoard";
import TriageBoard from "@/pages/TriageBoard";
import InspectionReportPage from "@/pages/InspectionReportPage";
import WyldecrestLogin from "@/pages/wyldecrest/WyldecrestLogin";
import WyldecrestReportsDashboard from "@/pages/wyldecrest/WyldecrestReportsDashboard";
import LhsControlDashboard from "@/pages/LhsControlDashboard";
import LHSHelpdeskView from "@/pages/lhs/LHSHelpdeskView";
import ClientDashboardProduction from "@/pages/client/ClientDashboardProduction";
import PWAUpdateHandler from "@/components/PWAUpdateHandler";
import ProjectsPage from "@/pages/ProjectsPage";
import PHSMembersPage from "@/pages/PHSMembersPage";
import DispatchBoard from "@/pages/DispatchBoard";
import PHSPayInvoicePage from "@/pages/PHSPayInvoicePage";
import PHSPaymentSuccessPage from "@/pages/PHSPaymentSuccessPage";
import PHSQuoteAcceptPage from "@/pages/PHSQuoteAcceptPage";
import PHSQuoteMultiOptionPage from "@/pages/PHSQuoteMultiOptionPage";
import FinanceDashboard from "@/pages/FinanceDashboard";
import ContractorRFQPage from "@/pages/ContractorRFQPage";
import RFQManagementPage from "@/pages/RFQManagementPage";
import ContractorMobileDashboard from "@/pages/ContractorMobileDashboard";
import ContractorMobileJobPage from "@/pages/ContractorMobileJobPage";

// PHS Internal Operations Pages - CONSOLIDATED
import BenchmarkingPage from "@/pages/phs/BenchmarkingPage";
import RateLibraryPage from "@/pages/phs/RateLibraryPage";
import PHSPPMLibraryPage from "@/pages/phs/PPMLibraryPage";
import CostAnalyticsPage from "@/pages/phs/CostAnalyticsPage";
import PHSJobWorkspace from "@/pages/phs/PHSJobWorkspace";
import OperationalBoardView from "@/pages/phs/OperationalBoardView";
import OperationalHealthDashboard from "@/pages/phs/OperationalHealthDashboard";
import PHSFinancePage from "@/pages/phs/FinancePage";
// 5 CORE WORKSPACES
import AssetWorkspace from "@/pages/phs/AssetWorkspace";
import ContractorWorkspace from "@/pages/phs/ContractorWorkspace";
import OperationalMonitoringPage from "@/pages/OperationalMonitoringPage";
import ContractorMagicLinkPage from "@/pages/ContractorMagicLinkPage";
import SeedUsersPage from "@/pages/SeedUsersPage";

// PHS Governance Pages
import {
  GovernanceHub,
  InternalSOPsPage,
  ContractorStandardsPage,
  EvidenceGovernancePage,
  FinanceGovernancePage,
  IncidentManagementPage,
  ClientOnboardingPage,
  DeploymentGovernancePage,
  PilotRolloutPage,
  HelpdeskUserGuidePage
} from "@/pages/phs/governance";

// PHS Admin Login
import PHSAdminLogin from "@/pages/admin/PHSAdminLogin";

// Enterprise Helpdesk View
import EnterpriseHelpdeskView from "@/pages/wyldecrest/EnterpriseHelpdeskView";

// =============================================================================
// ROLE-BASED ROUTE UTILITIES
// =============================================================================

const getDefaultRouteForRole = (role) => {
  switch (role) {
    case "super_admin":
    case "admin":
    case "director":
    case "manager":
      return "/admin";
    case "client_admin":
    case "ops_manager":
      return "/wyldecrest";
    case "helpdesk":
      return "/helpdesk";
    case "contractor_company_admin":
    case "contractor_mobile":
    case "contractor_user":
      return "/contractor";
    case "member":
      return "/member";
    case "park_manager":
      return "/portal";
    default:
      return "/admin";
  }
};

const isRoleAllowedForRoute = (role, routePrefix) => {
  const rules = {
    "/admin": ["super_admin", "admin", "director", "manager", "surveyor"],
    "/wyldecrest": ["client_admin", "ops_manager", "helpdesk", "super_admin", "admin"],
    "/contractor": ["contractor_company_admin", "contractor_mobile", "contractor_user"],
    "/member": ["member"],
    "/helpdesk": ["helpdesk", "ops_manager", "client_admin", "super_admin", "admin"],
    "/portal": ["park_manager"]
  };
  return rules[routePrefix]?.includes(role) || false;
};

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-teal-50/30">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007073]"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect park_manager users to portal
  if (user.role === 'park_manager') {
    return <Navigate to="/portal" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

// Park Manager Portal Route - No Layout wrapper (Portal has its own layout)
const PortalRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-teal-50/30">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007073]"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Only park_manager role can access portal routes
  if (user.role !== 'park_manager') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const SurveyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-teal-50/30">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007073]"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-teal-50/30">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007073]"></div>
      </div>
    );
  }
  
  return (
    <Routes>
      {/* Public Landing Page */}
      <Route path="/home" element={<LandingPage />} />
      
      <Route path="/login" element={user ? <Navigate to={user.role === 'park_manager' ? '/portal' : (user.role === 'helpdesk' || user.role === 'admin' || user.role === 'super_admin' ? '/phs/helpdesk' : '/dashboard')} replace /> : <LoginPage />} />
      
      {/* Dashboard - Redirect helpdesk users to PHS Helpdesk */}
      <Route path="/" element={<ProtectedRoute><Navigate to="/phs/helpdesk" replace /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Navigate to="/phs/helpdesk" replace /></ProtectedRoute>} />
      
      {/* Core CRM */}
      <Route path="/leads" element={<ProtectedRoute><LeadsPage /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><ClientsPage /></ProtectedRoute>} />
      <Route path="/parks" element={<ProtectedRoute><ParksPage /></ProtectedRoute>} />
      
      {/* Jobs & Workflow */}
      <Route path="/jobs" element={<ProtectedRoute><JobsPage /></ProtectedRoute>} />
      <Route path="/survey/:jobId" element={<SurveyRoute><MobileSurveyPage /></SurveyRoute>} />
      
      {/* Documents */}
      <Route path="/quotes" element={<ProtectedRoute><QuotesPage /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
      
      {/* Legacy (still available) */}
      <Route path="/cases" element={<ProtectedRoute><CasesPage /></ProtectedRoute>} />
      <Route path="/claims" element={<ProtectedRoute><ClaimsPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/reporting" element={<ProtectedRoute><ReportingDashboardPage /></ProtectedRoute>} />
      
      {/* Job Detail */}
      <Route path="/jobs/:jobId" element={<ProtectedRoute><JobDetailPage /></ProtectedRoute>} />
      
      {/* Resources */}
      <Route path="/contractors" element={<ProtectedRoute><ContractorsPage /></ProtectedRoute>} />
      <Route path="/contractors/:id" element={<ProtectedRoute><ContractorProfilePage /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
      
      {/* Admin Only */}
      <Route path="/staff" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><StaffPage /></ProtectedRoute>} />
      <Route path="/survey-templates" element={<ProtectedRoute allowedRoles={['admin']}><SurveyTemplatesPage /></ProtectedRoute>} />
      <Route path="/portal-management" element={<ProtectedRoute allowedRoles={['admin', 'surveyor', 'manager', 'director']}><ClientPortalManagementPage /></ProtectedRoute>} />
      
      {/* Settings */}
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      
      {/* Calendar */}
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      
      {/* FM Operating System */}
      <Route path="/fm" element={<ProtectedRoute><FMDashboardPage /></ProtectedRoute>} />
      <Route path="/fm/jobs/:jobId" element={<ProtectedRoute><FMJobDetailPage /></ProtectedRoute>} />
      
      {/* Projects */}
      <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
      
      {/* PHS Members */}
      <Route path="/phs-members" element={<ProtectedRoute><PHSMembersPage /></ProtectedRoute>} />
      
      {/* Dispatch Board - Operations Command Centre */}
      <Route path="/dispatch" element={<ProtectedRoute><DispatchBoard /></ProtectedRoute>} />
      
      {/* Operations & Compliance Dashboard */}
      <Route path="/operations" element={<ProtectedRoute><OperationalDashboardPage /></ProtectedRoute>} />
      
      {/* Finance Control Dashboard */}
      <Route path="/finance" element={<ProtectedRoute><FinanceDashboard /></ProtectedRoute>} />
      
      {/* RFQ Management - Internal */}
      <Route path="/rfq/:rfqId" element={<ProtectedRoute><RFQManagementPage /></ProtectedRoute>} />
      
      {/* PPM Schedule System */}
      <Route path="/ppm" element={<ProtectedRoute><PPMDashboardPage /></ProtectedRoute>} />
      <Route path="/ppm-library" element={<ProtectedRoute><PPMLibraryPage /></ProtectedRoute>} />
      <Route path="/ppm/library" element={<ProtectedRoute><PPMLibraryPage /></ProtectedRoute>} />
      <Route path="/ppm-schedule" element={<ProtectedRoute><PPMSchedulePage /></ProtectedRoute>} />
      <Route path="/ppm/schedule" element={<ProtectedRoute><PPMSchedulePage /></ProtectedRoute>} />
      <Route path="/ppm-jobs" element={<ProtectedRoute><PPMJobsPage /></ProtectedRoute>} />
      <Route path="/ppm/jobs" element={<ProtectedRoute><PPMJobsPage /></ProtectedRoute>} />
      <Route path="/assets" element={<ProtectedRoute><AssetsPage /></ProtectedRoute>} />
      <Route path="/assets/:assetId" element={<ProtectedRoute><AssetDetailPage /></ProtectedRoute>} />
      
      {/* Contract Pricing System */}
      <Route path="/contracts" element={<ProtectedRoute><ContractsDashboardPage /></ProtectedRoute>} />
      
      {/* Commercial Control System */}
      <Route path="/quoted-works" element={<ProtectedRoute><QuotedWorksPage /></ProtectedRoute>} />
      <Route path="/variations" element={<ProtectedRoute><VariationsPage /></ProtectedRoute>} />
      <Route path="/invoice-control" element={<ProtectedRoute><InvoiceControlPage /></ProtectedRoute>} />
      
      {/* Client Reporting Portal */}
      <Route path="/client-reports" element={<ProtectedRoute><ClientReportingPage /></ProtectedRoute>} />
      
      {/* Operations - Routes, Attendance, Automation */}
      <Route path="/operations/routes" element={<ProtectedRoute><OperationsRoutesPage /></ProtectedRoute>} />
      <Route path="/operations/attendance" element={<ProtectedRoute><OperationsAttendancePage /></ProtectedRoute>} />
      <Route path="/operations/automation" element={<ProtectedRoute><OperationsAutomationPage /></ProtectedRoute>} />
      <Route path="/operations/communication" element={<ProtectedRoute><CommunicationMetricsPage /></ProtectedRoute>} />
      <Route path="/communication-metrics" element={<ProtectedRoute><CommunicationMetricsPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AdvancedAnalyticsPage /></ProtectedRoute>} />
      <Route path="/fm/reporting" element={<ProtectedRoute><FMReportingPage /></ProtectedRoute>} />
      <Route path="/reporting" element={<ProtectedRoute><FMReportingPage /></ProtectedRoute>} />
      <Route path="/contractors/:contractorId/performance" element={<ProtectedRoute><ContractorPerformanceDetailPage /></ProtectedRoute>} />
      <Route path="/client/report" element={<ProtectedRoute><ClientReportPage /></ProtectedRoute>} />
      <Route path="/clients/:clientId/report" element={<ProtectedRoute><ClientReportPage /></ProtectedRoute>} />
      <Route path="/live-issues" element={<ProtectedRoute><LiveIssueTrackerPage /></ProtectedRoute>} />
      <Route path="/issues" element={<ProtectedRoute><LiveIssueTrackerPage /></ProtectedRoute>} />
      
      {/* Smart Job Intake System */}
      <Route path="/fm/intake" element={<ProtectedRoute><SmartJobIntakePage /></ProtectedRoute>} />
      <Route path="/fm/quick-create" element={<ProtectedRoute><SmartJobIntakePage /></ProtectedRoute>} />
      <Route path="/mobile/report" element={<ProtectedRoute><MobileQuickReportPage /></ProtectedRoute>} />
      <Route path="/client/request" element={<ClientJobRequestPage />} />
      
      {/* Readiness Audit */}
      <Route path="/readiness" element={<ProtectedRoute><ReadinessAuditPage /></ProtectedRoute>} />
      <Route path="/readiness-audit" element={<ProtectedRoute><ReadinessAuditPage /></ProtectedRoute>} />
      
      {/* Admin Tools */}
      <Route path="/admin/data-cleanup" element={<ProtectedRoute><DataCleanupPage /></ProtectedRoute>} />
      <Route path="/admin/test-job-purge" element={<ProtectedRoute><TestJobPurgePage /></ProtectedRoute>} />
      <Route path="/admin/data-merge" element={<ProtectedRoute><DataMergeDashboardPage /></ProtectedRoute>} />
      <Route path="/admin/lockdown" element={<ProtectedRoute><LockdownDashboardPage /></ProtectedRoute>} />
      <Route path="/lockdown" element={<ProtectedRoute><LockdownDashboardPage /></ProtectedRoute>} />
      <Route path="/admin/test-data-purge" element={<ProtectedRoute><TestDataPurgePage /></ProtectedRoute>} />
      <Route path="/test-data-purge" element={<ProtectedRoute><TestDataPurgePage /></ProtectedRoute>} />
      <Route path="/admin/quick-fix" element={<ProtectedRoute><QuickFixWizardPage /></ProtectedRoute>} />
      <Route path="/quick-fix" element={<ProtectedRoute><QuickFixWizardPage /></ProtectedRoute>} />
      <Route path="/admin/pressure-test" element={<ProtectedRoute><PressureTestPage /></ProtectedRoute>} />
      <Route path="/pressure-test" element={<ProtectedRoute><PressureTestPage /></ProtectedRoute>} />
      <Route path="/edge-cases" element={<ProtectedRoute><EdgeCaseAuditPage /></ProtectedRoute>} />
      <Route path="/admin/edge-cases" element={<ProtectedRoute><EdgeCaseAuditPage /></ProtectedRoute>} />
      
      {/* LHS Command Centre */}
      <Route path="/lhs/control" element={<ProtectedRoute><LHSCommandCentre /></ProtectedRoute>} />
      <Route path="/command-centre" element={<ProtectedRoute><LHSCommandCentre /></ProtectedRoute>} />
      <Route path="/lhs-command" element={<ProtectedRoute><LHSCommandCentre /></ProtectedRoute>} />
      
      {/* LHS Production Helpdesk - New State Machine UI */}
      <Route path="/lhs/helpdesk" element={<LHSHelpdeskView />} />
      <Route path="/lhs-helpdesk" element={<LHSHelpdeskView />} />
      
      {/* Board Pack Dashboard */}
      <Route path="/board-pack" element={<ProtectedRoute><BoardPackDashboard /></ProtectedRoute>} />
      <Route path="/lhs/board-pack" element={<ProtectedRoute><BoardPackDashboard /></ProtectedRoute>} />
      
      {/* Pipeline Board */}
      <Route path="/pipeline" element={<ProtectedRoute><PipelineBoard /></ProtectedRoute>} />
      
      {/* Enquiry Triage Board */}
      <Route path="/triage" element={<ProtectedRoute><TriageBoard /></ProtectedRoute>} />
      
      {/* Inspection Reports */}
      <Route path="/inspection-reports" element={<ProtectedRoute><InspectionReportPage /></ProtectedRoute>} />
      
      {/* Contractor Company Portal (separate auth - no ProtectedRoute) */}
      <Route path="/contractor-company/login" element={<ContractorCompanyLogin />} />
      <Route path="/contractor-company/dashboard" element={<ContractorCompanyDashboard />} />
      <Route path="/contractor-company/jobs" element={<ContractorCompanyJobs />} />
      <Route path="/contractor-company/jobs/:jobId" element={<ContractorJobDetail />} />
      <Route path="/contractor-company/performance" element={<ContractorCompanyPerformance />} />
      
      <Route path="/ppm/compliance" element={<ProtectedRoute><PPMCompliancePage /></ProtectedRoute>} />
      <Route path="/ppm/workload" element={<ProtectedRoute><PPMWorkloadPage /></ProtectedRoute>} />
      <Route path="/ppm/bulk-assign" element={<ProtectedRoute><PPMBulkAssignPage /></ProtectedRoute>} />
      
      {/* Legacy Client Portal V2 Routes (Staff Auth) */}
      <Route path="/client-v2-legacy" element={<ClientPortalV2DashboardPage />} />
      <Route path="/client-v2-legacy/jobs" element={<ClientPortalV2JobsPage />} />
      <Route path="/client-v2-legacy/ppm" element={<ClientPortalV2PPMPage />} />
      <Route path="/client-v2-legacy/quotes" element={<ClientPortalV2QuotesPage />} />
      
      {/* Payment Success */}
      <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
      
      {/* Public Routes - No Auth Required */}
      <Route path="/quote-response" element={<QuoteResponsePage />} />
      
      {/* PHS Public Payment Routes - Token Based (No Auth Required) */}
      <Route path="/quote" element={<PHSQuoteAcceptPage />} />
      <Route path="/quote-options" element={<PHSQuoteMultiOptionPage />} />
      <Route path="/pay-invoice" element={<PHSPayInvoicePage />} />
      <Route path="/phs-payment-success" element={<PHSPaymentSuccessPage />} />
      
      {/* Contractor RFQ Submission - Public Token Based */}
      <Route path="/contractor/rfq/:token" element={<ContractorRFQPage />} />
      
      {/* Portal Routes - Separate Auth */}
      <Route path="/contractor-portal" element={<ContractorPortalPage />} />
      <Route path="/contractor" element={<ContractorPortalPage />} />
      <Route path="/client-portal" element={<ClientPortalPage />} />
      
      {/* Park Manager Portal - Role-based protected routes */}
      <Route path="/portal" element={<PortalRoute><PortalDashboardPage /></PortalRoute>} />
      <Route path="/portal/jobs" element={<PortalRoute><PortalJobsPage /></PortalRoute>} />
      <Route path="/portal/report" element={<PortalRoute><PortalReportPage /></PortalRoute>} />
      <Route path="/portal/ppm" element={<PortalRoute><PortalPPMPage /></PortalRoute>} />
      
      {/* Contractor Magic Link - Public Routes (No Auth Required) */}
      <Route path="/contractor/job/:jobId" element={<ContractorJobPage />} />
      <Route path="/contractor/jobs" element={<ContractorJobsListPage />} />
      <Route path="/contractor/action-queue" element={<GlobalActionQueuePage />} />
      <Route path="/contractor/respond/:jobId" element={<ContractorRespondPage />} />
      
      {/* Contractor Mobile UX - Field Engineer App */}
      <Route path="/contractor/mobile" element={<ContractorMobileDashboard />} />
      <Route path="/contractor/mobile/job/:jobId" element={<ContractorMobileJobPage />} />
      
      {/* Operational Monitoring - Admin Only */}
      <Route path="/monitoring" element={<ProtectedRoute><OperationalMonitoringPage /></ProtectedRoute>} />
      
      {/* Database Seed Page - Public */}
      <Route path="/seed-users" element={<SeedUsersPage />} />
      
      {/* Client Portal V2 - JWT Auth Routes (No Staff Auth Required) */}
      <Route path="/client-login" element={<ClientLoginPage />} />
      <Route path="/client-dashboard" element={<ClientPortalV2AuthDashboard />} />
      <Route path="/client-v2/jobs" element={<ClientJobsPage />} />
      <Route path="/client-v2/jobs/:jobId" element={<ClientJobDetailPage />} />
      <Route path="/client-v2/quotes" element={<ClientQuotesPage />} />
      <Route path="/client-v2/quotes/:quoteId" element={<ClientQuoteDetailPage />} />
      <Route path="/client-v2/invoices" element={<ClientInvoicesPage />} />
      <Route path="/client-v2/invoices/:invoiceId" element={<ClientInvoicesPage />} />
      <Route path="/client-v2/documents" element={<ClientDocumentsPage />} />
      
      {/* Wyldecrest Role-Based Portal (Separate Auth) */}
      <Route path="/wyldecrest/login" element={<WyldecrestLogin />} />
      <Route path="/wyldecrest" element={<Navigate to="/wyldecrest/login" replace />} />
      {/* LEGACY WYLDECREST ROUTES - Redirect to PHS equivalents */}
      <Route path="/wyldecrest/helpdesk/benchmarking" element={<Navigate to="/phs/benchmarking" replace />} />
      <Route path="/wyldecrest/helpdesk/rate-library" element={<Navigate to="/phs/rate-library" replace />} />
      <Route path="/wyldecrest/helpdesk/ppm-library" element={<Navigate to="/phs/ppm/library" replace />} />
      <Route path="/wyldecrest/helpdesk/cost-analytics" element={<Navigate to="/phs/health" replace />} />
      <Route path="/wyldecrest/helpdesk/contractor-performance" element={<Navigate to="/phs/contractors?tab=performance" replace />} />
      <Route path="/wyldecrest/helpdesk/health" element={<Navigate to="/phs/health" replace />} />
      <Route path="/wyldecrest/helpdesk/governance" element={<Navigate to="/phs/governance" replace />} />
      <Route path="/wyldecrest/helpdesk/sops" element={<Navigate to="/phs/governance/sops" replace />} />
      <Route path="/wyldecrest/helpdesk/contractor-standards" element={<Navigate to="/phs/governance" replace />} />
      <Route path="/wyldecrest/helpdesk/evidence-governance" element={<Navigate to="/phs/governance" replace />} />
      <Route path="/wyldecrest/helpdesk/finance-governance" element={<Navigate to="/phs/governance" replace />} />
      <Route path="/wyldecrest/helpdesk/incident-management" element={<Navigate to="/phs/governance" replace />} />
      <Route path="/wyldecrest/helpdesk/client-onboarding" element={<Navigate to="/phs/governance" replace />} />
      <Route path="/wyldecrest/helpdesk/deployment-governance" element={<Navigate to="/phs/governance" replace />} />
      <Route path="/wyldecrest/helpdesk/pilot-rollout" element={<Navigate to="/phs/governance" replace />} />
      <Route path="/wyldecrest/helpdesk/user-guide" element={<Navigate to="/phs/governance/user-guide" replace />} />
      <Route path="/wyldecrest/helpdesk/job/:jobId" element={<PHSJobWorkspace />} />
      <Route path="/wyldecrest/helpdesk/board" element={<Navigate to="/phs/helpdesk/board" replace />} />
      <Route path="/wyldecrest/helpdesk" element={<Navigate to="/phs/helpdesk" replace />} />
      {/* Redirect legacy Wyldecrest routes to clean PHS routes */}
      <Route path="/wyldecrest/ops" element={<Navigate to="/phs/helpdesk" replace />} />
      <Route path="/wyldecrest/dashboard" element={<Navigate to="/phs/helpdesk" replace />} />
      <Route path="/wyldecrest/contractor" element={<Navigate to="/phs/contractors" replace />} />
      <Route path="/wyldecrest/contractor-dashboard" element={<Navigate to="/phs/contractors" replace />} />
      <Route path="/wyldecrest/pipeline" element={<Navigate to="/phs/helpdesk/board" replace />} />
      <Route path="/wyldecrest/invoicing" element={<Navigate to="/phs/finance" replace />} />
      <Route path="/wyldecrest/sites" element={<Navigate to="/phs/assets?tab=parks" replace />} />
      <Route path="/wyldecrest/contractors" element={<Navigate to="/phs/contractors" replace />} />
      <Route path="/wyldecrest/reports" element={<Navigate to="/phs/reports" replace />} />
      
      {/* LHS Control Dashboard */}
      <Route path="/lhs/reports" element={<ProtectedRoute><LhsControlDashboard /></ProtectedRoute>} />
      
      {/* Production Client Dashboard - Sellable Client Layer */}
      <Route path="/client-portal-v3" element={<ClientDashboardProduction />} />
      <Route path="/lhs/client-dashboard" element={<ClientDashboardProduction />} />
      
      {/* Legacy Client Portal Routes */}
      <Route path="/client/dashboard" element={<ClientDashboardPage />} />
      <Route path="/client/jobs/:jobId" element={<ClientDashboardPage />} />
      
      {/* ================================================================== */}
      {/* CLEAN PRODUCTION ROUTES - Park Home Support                        */}
      {/* ================================================================== */}
      
      {/* ================================================================== */}
      {/* PHS UNIFIED OPERATIONS ROUTES                                     */}
      {/* WORKSPACE-DRIVEN ARCHITECTURE                                     */}
      {/* 5 Core Workspaces: Helpdesk, Job, Asset, Contractor, Finance      */}
      {/* ================================================================== */}
      
      {/* HELPDESK WORKSPACE - Operations Dashboard & Pipeline */}
      <Route path="/phs/helpdesk/board" element={<OperationalBoardView />} />
      <Route path="/phs/helpdesk/job/:jobId" element={<EnterpriseHelpdeskView />} />
      <Route path="/phs/helpdesk" element={<EnterpriseHelpdeskView />} />
      
      {/* ASSET WORKSPACE - Parks, Plots, Assets, PPM, Compliance (CONSOLIDATED) */}
      <Route path="/phs/assets" element={<AssetWorkspace />} />
      
      {/* CONTRACTOR WORKSPACE - Management, Performance, Compliance (CONSOLIDATED) */}
      <Route path="/phs/contractors" element={<ContractorWorkspace />} />
      
      {/* FINANCE WORKSPACE - Invoices, Payments, Financial Dashboard */}
      <Route path="/phs/finance" element={<PHSFinancePage />} />
      
      {/* Commercial Tools (remain standalone - low consolidation value) */}
      <Route path="/phs/benchmarking" element={<BenchmarkingPage />} />
      <Route path="/phs/rate-library" element={<RateLibraryPage />} />
      <Route path="/phs/ppm/library" element={<PHSPPMLibraryPage />} />
      
      {/* Governance (remain standalone) */}
      <Route path="/phs/governance/user-guide" element={<HelpdeskUserGuidePage />} />
      <Route path="/phs/governance/sops" element={<InternalSOPsPage />} />
      <Route path="/phs/governance" element={<GovernanceHub />} />
      
      {/* Reports */}
      <Route path="/phs/reports" element={<WyldecrestReportsDashboard />} />
      <Route path="/phs/health" element={<OperationalHealthDashboard />} />
      
      {/* ================================================================== */}
      {/* LEGACY ROUTE REDIRECTS - Consolidate into workspaces              */}
      {/* ================================================================== */}
      
      {/* Asset-related routes -> Asset Workspace */}
      <Route path="/phs/parks/:parkId" element={<Navigate to="/phs/assets?tab=parks" replace />} />
      <Route path="/phs/parks" element={<Navigate to="/phs/assets?tab=parks" replace />} />
      <Route path="/phs/plots" element={<Navigate to="/phs/assets?tab=plots" replace />} />
      <Route path="/phs/compliance/expiring" element={<Navigate to="/phs/assets?tab=compliance" replace />} />
      <Route path="/phs/compliance" element={<Navigate to="/phs/assets?tab=compliance" replace />} />
      <Route path="/phs/ppm/schedule" element={<Navigate to="/phs/assets?tab=ppm" replace />} />
      
      {/* Contractor-related routes -> Contractor Workspace */}
      <Route path="/phs/contractors/performance" element={<Navigate to="/phs/contractors?tab=performance" replace />} />
      <Route path="/phs/contractors/compliance" element={<Navigate to="/phs/contractors?tab=compliance" replace />} />
      
      {/* Legacy helpdesk routes */}
      <Route path="/phs/helpdesk/benchmarking" element={<Navigate to="/phs/benchmarking" replace />} />
      <Route path="/phs/helpdesk/rate-library" element={<Navigate to="/phs/rate-library" replace />} />
      <Route path="/phs/helpdesk/ppm-library" element={<Navigate to="/phs/ppm/library" replace />} />
      <Route path="/phs/helpdesk/cost-analytics" element={<Navigate to="/phs/health" replace />} />
      <Route path="/phs/helpdesk/contractor-performance" element={<Navigate to="/phs/contractors?tab=performance" replace />} />
      <Route path="/phs/helpdesk/health" element={<Navigate to="/phs/health" replace />} />
      <Route path="/phs/helpdesk/governance" element={<Navigate to="/phs/governance" replace />} />
      <Route path="/phs/helpdesk/sops" element={<Navigate to="/phs/governance/sops" replace />} />
      <Route path="/phs/helpdesk/user-guide" element={<Navigate to="/phs/governance/user-guide" replace />} />
      <Route path="/phs/helpdesk/contractor-standards" element={<ContractorStandardsPage />} />
      <Route path="/phs/helpdesk/evidence-governance" element={<EvidenceGovernancePage />} />
      <Route path="/phs/helpdesk/finance-governance" element={<FinanceGovernancePage />} />
      <Route path="/phs/helpdesk/incident-management" element={<IncidentManagementPage />} />
      <Route path="/phs/helpdesk/client-onboarding" element={<ClientOnboardingPage />} />
      <Route path="/phs/helpdesk/deployment-governance" element={<DeploymentGovernancePage />} />
      <Route path="/phs/helpdesk/pilot-rollout" element={<PilotRolloutPage />} />
      
      {/* Legacy Wyldecrest redirects */}
      <Route path="/phs/ops" element={<Navigate to="/phs/helpdesk" replace />} />
      <Route path="/phs/dashboard" element={<Navigate to="/phs/helpdesk" replace />} />
      <Route path="/phs/pipeline" element={<Navigate to="/phs/helpdesk/board" replace />} />
      <Route path="/phs/invoicing" element={<Navigate to="/phs/finance" replace />} />
      <Route path="/phs/sites" element={<Navigate to="/phs/assets?tab=parks" replace />} />
      <Route path="/phs/cost-analytics" element={<Navigate to="/phs/health" replace />} />
      <Route path="/phs/governance/training" element={<Navigate to="/phs/governance" replace />} />
      <Route path="/phs" element={<Navigate to="/admin/login" replace />} />
      
      {/* Admin Portal - /admin */}
      <Route path="/admin/login" element={<PHSAdminLogin />} />
      <Route path="/admin" element={user ? <Navigate to="/phs/helpdesk" replace /> : <Navigate to="/admin/login" replace />} />
      <Route path="/admin/*" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      
      {/* Contractor Portal - /contractor */}
      <Route path="/contractor" element={<ContractorCompanyLogin />} />
      <Route path="/contractor/login" element={<ContractorCompanyLogin />} />
      <Route path="/contractor/dashboard" element={<ContractorCompanyDashboard />} />
      <Route path="/contractor/magic/:token" element={<ContractorMagicLinkPage />} />
      
      {/* Member Portal - /member (placeholder for future) */}
      <Route path="/member" element={<Navigate to="/phs-members" replace />} />
      <Route path="/member/*" element={<Navigate to="/phs-members" replace />} />
      
      {/* Helpdesk Portal - /helpdesk */}
      <Route path="/helpdesk" element={<WyldecrestLogin />} />
      <Route path="/helpdesk/benchmarking" element={<BenchmarkingPage />} />
      <Route path="/helpdesk/rate-library" element={<RateLibraryPage />} />
      <Route path="/helpdesk/ppm-library" element={<PHSPPMLibraryPage />} />
      <Route path="/helpdesk/cost-analytics" element={<CostAnalyticsPage />} />
      <Route path="/helpdesk/contractor-performance" element={<Navigate to="/phs/contractors?tab=performance" replace />} />
      <Route path="/helpdesk/*" element={<EnterpriseHelpdeskView />} />
      
      {/* ================================================================== */}
      {/* COMPATIBILITY REDIRECTS - Keep old URLs working                    */}
      {/* ================================================================== */}
      
      {/* Old login routes -> Clean routes */}
      <Route path="/park-tracker" element={<Navigate to="/wyldecrest" replace />} />
      <Route path="/park-tracker/*" element={<Navigate to="/wyldecrest" replace />} />
      <Route path="/park-care-hub" element={<Navigate to="/member" replace />} />
      <Route path="/park-care-hub/*" element={<Navigate to="/member" replace />} />
      
      {/* Catch-all for 404 */}
      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
}

// PHS Branding Overlay - covers external badge
// Hidden on operational /phs screens
function PHSBrandingOverlay() {
  const location = window.location.pathname;
  
  // Hide on operational screens
  if (location.startsWith('/phs/') || location.startsWith('/helpdesk') || location.startsWith('/admin')) {
    return null;
  }
  
  return (
    <div 
      style={{
        position: 'fixed',
        bottom: '10px',
        right: '10px',
        zIndex: 2147483647,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 20px 10px 14px',
        backgroundColor: '#1F7A78',
        color: 'white',
        borderRadius: '28px',
        fontSize: '15px',
        fontWeight: '600',
        boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        letterSpacing: '0.3px',
      }}
    >
      <img 
        src="/phs-logo.png" 
        alt="PHS" 
        style={{ 
          height: '32px', 
          width: '32px', 
          borderRadius: '6px',
          objectFit: 'contain',
          backgroundColor: 'white',
          padding: '2px',
        }}
      />
      <span>Made with PHS</span>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WebSocketProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
          <PWAUpdateHandler />
          <PHSBrandingOverlay />
        </WebSocketProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
